import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Download, Check, ArrowRight } from 'lucide-react';
import { CONTACT, IMAGES, MENU } from '../mock/data';
import useScrollReveal from '../hooks/useScrollReveal';
import Seo from '../components/Seo';

export default function Dining() {
  const categories = Object.keys(MENU);
  const [active, setActive] = useState(categories[0]);
  useScrollReveal([active]);

  return (
    <div>
      <Seo
        title="Dining Menu — Western & Asian Comfort Food"
        path="/dining"
        description="Savour Vivamore's Western favourites (Burger, Chicken Chop, Spaghetti) and Asian signatures (Curry Mee, Ikan Bakar, Chicken Rice) plus private dining, on our rooftop at NU Sentral, KL."
        keywords="Vivamore menu, Burger KL, Chicken Chop, Spaghetti, Curry Mee, Ikan Bakar, Chicken Rice, rooftop dining NU Sentral, private dining Kuala Lumpur"
      />
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[420px] flex items-center justify-center overflow-hidden">
        <img src={IMAGES.heroDining} alt="Dining" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-ink/65" />
        <div className="relative z-10 text-center px-6">
          <span className="text-cream/80 text-xs tracking-luxe uppercase">Experience culinary excellence</span>
          <h1 className="font-display text-5xl md:text-6xl text-cream mt-5">Dining at Vivamore</h1>
        </div>
      </section>

      {/* Menu */}
      <section className="py-24 md:py-32 bg-cream">
        <div className="max-w-6xl mx-auto px-6">
          <div className="reveal text-center flex flex-col items-center mb-12">
            <span className="text-xs tracking-luxe uppercase text-gold">Our Menu</span>
            <h2 className="font-display text-4xl md:text-5xl text-ink mt-4">Culinary Creations</h2>
            <div className="gold-line mt-6" />
            <div className="flex flex-wrap gap-4 justify-center mt-8">
              <a href="/Vivamore-Menu.pdf" target="_blank" rel="noopener noreferrer" className="btn-gold inline-flex items-center gap-2 text-xs tracking-widest uppercase px-6 py-3 border border-gold text-gold hover:bg-gold hover:text-cream">
                <FileText size={15} /> View Menu
              </a>
              <a href="/Vivamore-Menu.pdf" download className="btn-gold inline-flex items-center gap-2 text-xs tracking-widest uppercase px-6 py-3 border border-ink text-ink hover:bg-ink hover:text-cream">
                <Download size={15} /> Download Menu
              </a>
            </div>
          </div>

          {/* Category tabs */}
          <div id="menu" className="flex flex-wrap justify-center gap-2 mb-12">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setActive(c)}
                className={`text-xs tracking-widest uppercase px-6 py-3 transition-all duration-300 ${
                  active === c ? 'bg-gold text-cream' : 'bg-transparent text-ink/60 hover:text-gold border border-ink/15'
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {MENU[active].map((item, i) => (
              <div key={item.name} className="reveal flex gap-6 items-center bg-white p-4 shadow-sm hover:shadow-md transition-shadow" style={{ transitionDelay: `${i * 0.08}s` }}>
                <div className="img-hover-zoom overflow-hidden w-28 h-28 shrink-0">
                  <img src={item.img} alt={item.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-baseline gap-3">
                    <h3 className="font-serif text-2xl text-ink">{item.name}</h3>
                    <span className="text-gold font-medium whitespace-nowrap">{item.price}</span>
                  </div>
                  <p className="text-ink/60 font-light text-sm mt-2 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-14">
            <a href={CONTACT.whatsappReserve} target="_blank" rel="noopener noreferrer" className="btn-gold inline-block text-xs tracking-widest uppercase px-9 py-4 bg-gold text-cream border border-gold hover:bg-transparent hover:text-gold">
              Make a Reservation
            </a>
          </div>
        </div>
      </section>

      {/* Private Dining */}
      <section className="relative py-32 overflow-hidden">
        <img src={IMAGES.privateDining1} alt="Private dining" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-ink/80" />
        <div className="relative z-10 max-w-2xl mx-auto px-6 reveal">
          <span className="text-xs tracking-luxe uppercase text-gold-light">Exclusive</span>
          <h2 className="font-display text-4xl md:text-5xl text-cream mt-4">Private Dining</h2>
          <div className="gold-line my-8" />
          <p className="text-cream/80 font-light leading-relaxed mb-8">
            For those seeking exclusivity, our private dining rooms offer an intimate setting for special occasions. Each room features elegant decor, dedicated service, and customizable menus.
          </p>
          <ul className="space-y-4 mb-10">
            {['Capacity: 10-20 guests per room', 'Customizable set menus available', 'Dedicated service team'].map((f) => (
              <li key={f} className="flex gap-3 text-cream/85 font-light"><Check size={18} className="text-gold-light shrink-0 mt-0.5" /> {f}</li>
            ))}
          </ul>
          <Link to="/plan-event" className="btn-gold inline-flex items-center gap-2 text-xs tracking-widest uppercase px-8 py-4 border border-gold text-gold hover:bg-gold hover:text-ink">
            Inquire Now <ArrowRight size={15} />
          </Link>
        </div>
      </section>
    </div>
  );
}
